import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "mapapet-language";

export const SUPPORTED_LANGUAGES = [
  "en",
  "ru",
  "de",
  "uk",
  "es",
  "pt",
  "fr",
  "it",
  "pl",
  "tr",
  "id",
  "hi",
  "ko",
  "ja",
  "zh",
  "ar",
];

/*
 * Явные dynamic import нужны намеренно.
 * Webpack создаёт отдельный chunk для каждого языка и не помещает
 * все 16 translation.json в стартовый main.js.
 */
const localeLoaders = {
  en: () =>
    import(
      /* webpackChunkName: "locale-en" */
      "./locales/en/translation.json"
    ),
  ru: () =>
    import(
      /* webpackChunkName: "locale-ru" */
      "./locales/ru/translation.json"
    ),
  de: () =>
    import(
      /* webpackChunkName: "locale-de" */
      "./locales/de/translation.json"
    ),
  uk: () =>
    import(
      /* webpackChunkName: "locale-uk" */
      "./locales/uk/translation.json"
    ),
  es: () =>
    import(
      /* webpackChunkName: "locale-es" */
      "./locales/es/translation.json"
    ),
  pt: () =>
    import(
      /* webpackChunkName: "locale-pt" */
      "./locales/pt/translation.json"
    ),
  fr: () =>
    import(
      /* webpackChunkName: "locale-fr" */
      "./locales/fr/translation.json"
    ),
  it: () =>
    import(
      /* webpackChunkName: "locale-it" */
      "./locales/it/translation.json"
    ),
  pl: () =>
    import(
      /* webpackChunkName: "locale-pl" */
      "./locales/pl/translation.json"
    ),
  tr: () =>
    import(
      /* webpackChunkName: "locale-tr" */
      "./locales/tr/translation.json"
    ),
  id: () =>
    import(
      /* webpackChunkName: "locale-id" */
      "./locales/id/translation.json"
    ),
  hi: () =>
    import(
      /* webpackChunkName: "locale-hi" */
      "./locales/hi/translation.json"
    ),
  ko: () =>
    import(
      /* webpackChunkName: "locale-ko" */
      "./locales/ko/translation.json"
    ),
  ja: () =>
    import(
      /* webpackChunkName: "locale-ja" */
      "./locales/ja/translation.json"
    ),
  zh: () =>
    import(
      /* webpackChunkName: "locale-zh" */
      "./locales/zh/translation.json"
    ),
  ar: () =>
    import(
      /* webpackChunkName: "locale-ar" */
      "./locales/ar/translation.json"
    ),
};

const localePromises = new Map();

const normalizeLanguage = (language) => {
  const normalized = String(language || "")
    .trim()
    .toLowerCase()
    .split("-")[0];

  return SUPPORTED_LANGUAGES.includes(normalized)
    ? normalized
    : "en";
};

const loadLocale = (language) => {
  const normalizedLanguage = normalizeLanguage(language);

  if (!localePromises.has(normalizedLanguage)) {
    const loader = localeLoaders[normalizedLanguage];

    const promise = loader()
      .then((module) => module.default || module)
      .catch((error) => {
        localePromises.delete(normalizedLanguage);
        throw error;
      });

    localePromises.set(normalizedLanguage, promise);
  }

  return localePromises.get(normalizedLanguage);
};

/*
 * Минимальный backend для i18next.
 * При старте загружается язык текущего поддомена и английский fallback.
 * При смене языка в localhost/preview нужный chunk догружается один раз.
 */
const dynamicLocaleBackend = {
  type: "backend",

  init() {},

  read(language, _namespace, callback) {
    loadLocale(language)
      .then((translations) => callback(null, translations))
      .catch((error) => callback(error, false));
  },
};

export const getLanguageFromHost = () => {
  if (typeof window === "undefined") return "en";

  const hostname = window.location.hostname.toLowerCase();

  const hostLanguages = {
    "ru.mapapet.com": "ru",
    "de.mapapet.com": "de",
    "uk.mapapet.com": "uk",
    "es.mapapet.com": "es",
    "pt.mapapet.com": "pt",
    "fr.mapapet.com": "fr",
    "it.mapapet.com": "it",
    "pl.mapapet.com": "pl",
    "tr.mapapet.com": "tr",
    "id.mapapet.com": "id",
    "hi.mapapet.com": "hi",
    "ko.mapapet.com": "ko",
    "ja.mapapet.com": "ja",
    "zh.mapapet.com": "zh",
    "ar.mapapet.com": "ar",
  };

  if (hostLanguages[hostname]) {
    return hostLanguages[hostname];
  }

  if (
    hostname === "mapapet.com" ||
    hostname === "www.mapapet.com"
  ) {
    return "en";
  }

  const saved = window.localStorage.getItem(STORAGE_KEY);

  return SUPPORTED_LANGUAGES.includes(saved)
    ? saved
    : "ru";
};

const updateDocumentLanguage = (language) => {
  if (typeof document === "undefined") return;

  const normalizedLanguage = normalizeLanguage(language);
  const direction =
    normalizedLanguage === "ar" ? "rtl" : "ltr";

  document.documentElement.lang = normalizedLanguage;
  document.documentElement.dir = direction;
  document.documentElement.dataset.direction = direction;

  if (document.body) {
    document.body.dir = direction;
    document.body.dataset.direction = direction;
  }

  const translatedTitle = i18n.t("seo.title");
  if (
    translatedTitle &&
    translatedTitle !== "seo.title"
  ) {
    document.title = translatedTitle;
  }

  const translatedDescription =
    i18n.t("seo.description");

  if (
    translatedDescription &&
    translatedDescription !== "seo.description"
  ) {
    let description = document.querySelector(
      'meta[name="description"]'
    );

    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    description.setAttribute(
      "content",
      translatedDescription
    );
  }
};

const initialLanguage = getLanguageFromHost();

i18n.on("languageChanged", (language) => {
  const normalizedLanguage =
    normalizeLanguage(language);

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      STORAGE_KEY,
      normalizedLanguage
    );
  }

  updateDocumentLanguage(normalizedLanguage);
});

export const i18nReady = i18n
  .use(dynamicLocaleBackend)
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",

    ns: ["translation"],
    defaultNS: "translation",

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  })
  .then(() => {
    updateDocumentLanguage(
      i18n.resolvedLanguage ||
        i18n.language ||
        initialLanguage
    );

    return i18n;
  });

export default i18n;
